import { createContext, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { normalizeSection } from "@/portal/PortalSections";

/* Robbie-App-Modus: Portal-Bereiche laufen unter /app/<slug>; jeder interne /portal#…-Link wird innerhalb der App auf /app/… umgeschrieben. */
export const AppModeContext = createContext(false);
export const useAppMode = () => useContext(AppModeContext);

// Portal-Section → App-Pfad (verständliche Slugs), Rückrichtung über SLUG_TO_SECTION
export const SECTION_TO_SLUG = {
  uebersicht: "", grafik: "grafik", social: "social", brain: "buero", projekte: "cloud", linkinbio: "mini-website", websites: "website", homepage: "baukasten", hosting: "hosting", dateien: "website-dateien", backups: "sicherungen",
  bildschirme: "bildschirme", tickets: "tickets", google: "google", robbie: "einstellungen", preise: "pakete", vertraege: "vertraege", zugaenge: "konto", hilfe: "hilfe",
};
export const SLUG_TO_SECTION = Object.fromEntries(Object.entries(SECTION_TO_SLUG).filter(([, v]) => v).map(([k, v]) => [v, k]));
export const sectionFromSlug = (slug) => SLUG_TO_SECTION[slug] || (slug && normalizeSection(slug) !== "uebersicht" ? normalizeSection(slug) : "uebersicht");

/** „#social/led“, „social/led“, „projekte/<pid>“, „brain/vorgaenge“ → { pathname: "/app/social", hash: "#social/led" } */
export const appTarget = (id, search = "") => {
  const h = (id || "").replace(/^#/, "");
  const top = normalizeSection(h);
  const slug = SECTION_TO_SLUG[top] ?? top;
  return { pathname: slug ? `/app/${slug}` : "/app", search, hash: h && h !== "uebersicht" ? `#${h}` : "" };
};

/** Portal-URL (href) → App-Ziel; null, wenn kein Portal-Link. */
export const portalHrefToApp = (href) => {
  let u;
  try { u = new URL(href, window.location.origin); } catch { return null; }
  if (u.origin !== window.location.origin) return null;
  if (u.pathname === "/portal" || u.pathname === "/portal/") {
    const bereich = u.searchParams.get("bereich") || u.searchParams.get("tab");
    const id = u.hash ? u.hash.slice(1) : bereich || "";
    u.searchParams.delete("bereich"); u.searchParams.delete("tab");
    return appTarget(id, u.searchParams.toString() ? `?${u.searchParams.toString()}` : "");
  }
  if (u.pathname === "/mein-tag") return { pathname: "/app", search: "", hash: "" };
  if (u.pathname === "/l" || u.pathname.startsWith("/l/")) { if (u.searchParams.get("z")) return null; return { pathname: `/app/listen${u.pathname.slice(2)}`, search: u.search, hash: u.hash }; }  // Listen in der App-Hülle (Mitwirkenden-Links mit Zugang bleiben eigenständig)
  if (u.pathname === "/mein-brain") return appTarget("brain");
  return null;
};

/** Fängt Klicks auf /portal-Links in der App ab (Capture-Phase, vor React) und bleibt in der App-Hülle. */
export const AppLinkGuard = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const h = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target?.closest?.("a[href]");
      if (!a || a.target === "_blank" || a.hasAttribute("download") || a.dataset.portalExplizit === "1") return;
      const ziel = portalHrefToApp(a.getAttribute("href"));
      if (!ziel) return;
      e.preventDefault(); e.stopPropagation();
      navigate(ziel);
      window.dispatchEvent(new window.HashChangeEvent("hashchange"));  // Unterbereiche (Social/Büro) lesen den Hash selbst
      window.scrollTo({ top: 0 });
    };
    document.addEventListener("click", h, true);
    return () => document.removeEventListener("click", h, true);
  }, [navigate]);
  return null;
};
